import React, { useCallback, useEffect, useState } from 'react';
import {
  Check,
  DatabaseBackup,
  FileDown,
  FolderCheck,
  FolderPlus,
  HardDriveDownload,
  ListChecks,
  LoaderCircle,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  ScrollText,
  Search,
  Server,
  Settings,
  Square,
  TestTubeDiagonal,
  Trash2,
  TriangleAlert,
  Wrench,
  X,
} from 'lucide-react';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import { formatDateTime } from '../../../Shared/Utils/HelperUtils';
import {
  createBackupConfig,
  cancelBackup,
  deleteBackupConfig,
  deleteBackupJob,
  downloadBackup,
  getBackupConfigs,
  getBackupJobs,
  getBackupJob,
  getBackupTables,
  setupBackupFolder,
  takeBackup,
  testBackupConfig,
  updateBackupConfig,
} from '../../Services/BackupService';
import {
  BACKUP_ENVIRONMENTS,
  BACKUP_ENV_LABELS,
  BACKUP_FORMATS,
  BACKUP_FORMAT_HELP,
  BACKUP_FORMAT_LABELS,
  BACKUP_STATUSES,
  BACKUP_STATUS_LABELS,
  BackupConfig,
  BackupConfigRequest,
  BackupFormat,
  BackupJob,
  BackupStatus,
  TakeBackupRequest,
  isSqlFormat,
  sqlTypeOfFormat,
} from '../../Types/BackupType';
import SourceBackupComponent from './SourceBackupComponent';

const PAGE_SIZE = 10;

const sourceTabs: {
  key: 'DB' | 'JENKINS' | 'MAILHOG';
  label: string;
  icon: React.ComponentType<{ size?: number }>;
}[] = [
  { key: 'DB', label: 'DB Backup', icon: DatabaseBackup },
  { key: 'JENKINS', label: 'Jenkins Backup', icon: Wrench },
  { key: 'MAILHOG', label: 'MailHog Backup', icon: Mail },
];

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const headerClass = 'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const statusTone: Record<BackupStatus, string> = {
  SUCCESS: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  FAILED: 'border-rose-500/30 bg-rose-500/10 text-rose-400',
  RUNNING: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  PENDING: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  CANCELLED: 'border-slate-600 bg-slate-800/60 text-slate-400',
};

const emptyForm: BackupConfigRequest = {
  name: '',
  environment: 'LOCAL',
  host: 'localhost',
  port: 3306,
  databaseName: '',
  username: '',
  password: '',
  baseFolderPath: '',
  active: true,
};

const formatBytes = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

/** Compact duration for the ETA and elapsed counters, e.g. "3m 20s", "45s", "800ms". */
const formatDuration = (millis?: number | null): string => {
  if (millis === null || millis === undefined || millis < 0) return '—';
  if (millis < 1000) return `${Math.round(millis)}ms`;
  const totalSeconds = Math.round(millis / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

/**
 * Fallback ZIP name when the server's Content-Disposition cannot be read: the run's start
 * time in words plus the job id, e.g.
 * "nexgent-backup_October-18-2026_at-12-44-PM_9905....zip".
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const downloadFallbackName = (job: BackupJob): string => {
  const raw = job.startedAt || job.createdAt;
  if (!raw) return `nexgent-backup_${job.id}.zip`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return `nexgent-backup_${job.id}.zip`;
  const month = MONTHS[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  const hours12 = date.getHours() % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = date.getHours() < 12 ? 'AM' : 'PM';
  return `nexgent-backup_${month}-${day}-${year}_at-${hours12}-${minutes}-${ampm}_${job.id}.zip`;
};

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[10px] font-semibold text-slate-500">{hint}</p>}
  </label>
);

const FormatOption = ({
  format,
  checked,
  onToggle,
}: {
  format: BackupFormat;
  checked: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
      checked
        ? 'border-cyan-500/40 bg-cyan-500/10'
        : 'border-slate-800 bg-slate-950/60 hover:border-slate-700'
    }`}
  >
    <span
      className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
        checked ? 'border-cyan-400 bg-cyan-500 text-slate-950' : 'border-slate-600'
      }`}
    >
      {checked && <Check size={11} strokeWidth={3} />}
    </span>
    <span>
      <span className="block text-xs font-bold text-slate-100">
        {BACKUP_FORMAT_LABELS[format]}
      </span>
      <span className="block text-[10px] font-semibold text-slate-500">
        {BACKUP_FORMAT_HELP[format]}
      </span>
    </span>
  </button>
);

/**
 * Business Support > Take Backup.
 *
 * Two tabs. Backups lists every run with filters and offers the Take Backup trigger; DB Config
 * owns the per-environment credentials and the folder their dumps land in. Take Backup stays
 * disabled while the selected configuration's folder is absent, and the panel offers Setup
 * Folder so the gap can be fixed from the same screen.
 */
function TakeBackupComponent() {
  const [source, setSource] = useState<'DB' | 'JENKINS' | 'MAILHOG'>('DB');
  const [tab, setTab] = useState<'backups' | 'config'>('backups');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  // ---- Runs -------------------------------------------------------
  const [jobs, setJobs] = useState<PaginationResponse<BackupJob> | null>(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formatFilter, setFormatFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [confirmJobId, setConfirmJobId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // ---- Audit log viewer --------------------------------------------
  const [logsJobId, setLogsJobId] = useState<string | null>(null);
  const [logsJob, setLogsJob] = useState<BackupJob | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // ---- Trigger ----------------------------------------------------
  const [configs, setConfigs] = useState<BackupConfig[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [formats, setFormats] = useState<BackupFormat[]>(['CSV']);
  const [taking, setTaking] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);

  // ---- Table picker ------------------------------------------------
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tableList, setTableList] = useState<string[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [pickedTables, setPickedTables] = useState<Set<string>>(new Set());
  const [tablePickerError, setTablePickerError] = useState('');

  // ---- Live run ----------------------------------------------------
  const [liveJob, setLiveJob] = useState<BackupJob | null>(null);
  const [stopping, setStopping] = useState(false);

  // ---- Config form -------------------------------------------------
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BackupConfig | null>(null);
  const [form, setForm] = useState<BackupConfigRequest>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [confirmConfigId, setConfirmConfigId] = useState<string | null>(null);

  const selected = configs.find((config) => config.id === selectedId) || null;

  const showError = useCallback((message: string) => {
    setAlert({ variant: 'error', message });
  }, []);

  const loadConfigs = useCallback(async () => {
    const response = await getBackupConfigs();
    if (response.success) {
      const list = response.data || [];
      setConfigs(list);
      setSelectedId((previous) => {
        if (previous && list.some((config) => config.id === previous)) return previous;
        return list.length ? list[0].id : '';
      });
    } else {
      showError(response.message);
    }
  }, [showError]);

  const loadJobs = useCallback(
    async (targetPage: number) => {
      setJobsLoading(true);
      const response = await getBackupJobs({
        backupType: 'DB',
        page: targetPage,
        size: PAGE_SIZE,
        environment: envFilter,
        status: statusFilter,
        format: formatFilter,
        search,
        fromDate,
        toDate,
      });
      setJobsLoading(false);
      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        showError(response.message);
      }
    },
    [envFilter, statusFilter, formatFilter, search, fromDate, toDate, showError]
  );

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  // A table selection is only meaningful for one database.
  useEffect(() => {
    setPickedTables(new Set());
    setTableList([]);
  }, [selectedId]);

  useEffect(() => {
    loadJobs(page);
  }, [loadJobs, page]);

  // Refresh the list while a run is still in flight.
  const isRunning = Boolean(
    jobs && jobs.content.some((job) => job.status === 'RUNNING' || job.status === 'PENDING')
  );
  useEffect(() => {
    if (!isRunning) return undefined;
    const timer = window.setInterval(() => loadJobs(page), 5000);
    return () => window.clearInterval(timer);
  }, [isRunning, page, loadJobs]);

  const runningJob =
    jobs?.content.find((job) => job.status === 'RUNNING' || job.status === 'PENDING') || null;

  // Poll the in-flight run every 2s for the percentage bar, ETA and audit lines.
  useEffect(() => {
    if (!runningJob) {
      setLiveJob(null);
      return undefined;
    }
    let active = true;
    const poll = async () => {
      const response = await getBackupJob(runningJob.id);
      if (active && response.success && response.data) setLiveJob(response.data);
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [runningJob?.id]);

  const resetPage = (apply: () => void) => {
    apply();
    setPage(0);
  };

  const toggleFormat = (format: BackupFormat) =>
    setFormats((previous) =>
      previous.includes(format)
        ? previous.filter((item) => item !== format)
        : [...previous, format]
    );

  const canTakeBackup = Boolean(selected) && selected?.folderExists && formats.length > 0;

  // An empty selection means "every table", so the payload only carries names when the picker
  // was narrowed.
  const selectionLabel =
    pickedTables.size === 0 ? 'All tables' : `${pickedTables.size} table(s) selected`;

  const openTablePicker = async () => {
    if (!selected) return;
    setPickerOpen(true);
    setTablePickerError('');
    setTableSearch('');
    setTablesLoading(true);
    setTableList([]);
    const response = await getBackupTables(selected.id);
    setTablesLoading(false);
    if (response.success && response.data) {
      const names = response.data;
      setTableList(names);
      // Keep an existing selection; otherwise start with every table ticked.
      setPickedTables((previous) => {
        if (previous.size > 0) {
          const kept = new Set<string>();
          names.forEach((name) => {
            if (previous.has(name)) kept.add(name);
          });
          return kept;
        }
        return new Set(names);
      });
    } else {
      setTablePickerError(response.message || 'Could not read the table list.');
    }
  };

  const closeTablePicker = () => setPickerOpen(false);

  const toggleTable = (name: string) =>
    setPickedTables((previous) => {
      const next = new Set(previous);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const visibleTables = tableSearch.trim()
    ? tableList.filter((name) => name.toLowerCase().includes(tableSearch.trim().toLowerCase()))
    : tableList;

  const selectAllVisible = () =>
    setPickedTables((previous) => {
      const next = new Set(previous);
      visibleTables.forEach((name) => next.add(name));
      return next;
    });

  const deselectAllVisible = () =>
    setPickedTables((previous) => {
      const next = new Set(previous);
      visibleTables.forEach((name) => next.delete(name));
      return next;
    });

  const applyTableSelection = () => {
    // Full selection collapses back to "all tables" so future tables are picked up too.
    if (pickedTables.size === tableList.length) setPickedTables(new Set());
    setPickerOpen(false);
  };

  const handleTakeBackup = async () => {
    if (!selected || !canTakeBackup) return;
    setTaking(true);
    const payload: TakeBackupRequest = {
      dbConfigId: selected.id,
      formats,
      selectedTables: Array.from(pickedTables),
    };
    const sqlFormat = formats.find((format) => isSqlFormat(format));
    if (sqlFormat) payload.sqlBackupType = sqlTypeOfFormat(sqlFormat) || undefined;
    const response = await takeBackup(payload);
    setTaking(false);
    if (response.success && response.data) {
      setAlert({
        variant: 'success',
        title: 'Backup started',
        message: `Writing ${formats.length} format(s) for ${response.data.dbName}.`,
      });
      setPickedTables(new Set());
      setPage(0);
      loadJobs(0);
      setTab('backups');
    } else {
      showError(response.message);
    }
  };

  const handleStopRun = async () => {
    if (!runningJob) return;
    setStopping(true);
    const response = await cancelBackup(runningJob.id);
    setStopping(false);
    if (response.success) {
      setAlert({
        variant: 'success',
        title: 'Stop requested',
        message: 'The run will halt at the next table boundary.',
      });
      loadJobs(page);
    } else {
      showError(response.message);
    }
  };

  /**
   * Opens the per-run audit log. Runs keep their table-by-table lines on the job row, so a
   * finished backup's log stays available here even after the live panel is gone.
   */
  const openJobLogs = async (job: BackupJob) => {
    setLogsJobId(job.id);
    setLogsJob(job);
    setLogsLoading(true);
    const response = await getBackupJob(job.id);
    setLogsLoading(false);
    if (response.success && response.data) {
      setLogsJob(response.data);
    } else {
      showError(response.message);
      setLogsJobId(null);
    }
  };

  const closeJobLogs = () => {
    setLogsJobId(null);
    setLogsJob(null);
  };

  const handleSetupFolder = async () => {
    if (!selected) return;
    setFolderBusy(true);
    const response = await setupBackupFolder(selected.id);
    setFolderBusy(false);
    if (response.success && response.data && response.data.exists) {
      setAlert({ variant: 'success', title: 'Folder created', message: response.data.message });
      loadConfigs();
    } else {
      showError(response.message || 'Could not create the folder.');
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (config: BackupConfig) => {
    setEditing(config);
    setForm({
      name: config.name,
      environment: config.environment,
      host: config.host,
      port: config.port,
      databaseName: config.databaseName,
      username: config.username,
      password: '',
      baseFolderPath: config.baseFolderPath,
      active: config.active,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const saveForm = async () => {
    setSaving(true);
    const response = editing
      ? await updateBackupConfig(editing.id, form)
      : await createBackupConfig(form);
    setSaving(false);
    if (response.success) {
      setAlert({
        variant: 'success',
        title: editing ? 'Saved' : 'Added',
        message: editing
          ? `${form.name} updated.`
          : `${form.name} added for ${form.environment}.`,
      });
      closeForm();
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    const response = await testBackupConfig(id);
    setTestingId(null);
    if (response.success) {
      setAlert({ variant: 'success', title: 'Connected', message: 'Database reachable.' });
    } else {
      showError(response.message);
    }
  };

  const handleDeleteConfig = async (id: string) => {
    setConfirmConfigId(null);
    const response = await deleteBackupConfig(id);
    if (response.success) {
      setAlert({ variant: 'success', title: 'Deleted', message: 'Configuration removed.' });
      loadConfigs();
    } else {
      showError(response.message);
    }
  };

  const handleDeleteJob = async (id: string) => {
    setConfirmJobId(null);
    const response = await deleteBackupJob(id);
    if (response.success) {
      setAlert({ variant: 'success', title: 'Deleted', message: 'Run removed from history.' });
      loadJobs(page);
    } else {
      showError(response.message);
    }
  };

  const handleDownload = async (job: BackupJob) => {
    setDownloadingId(job.id);
    try {
      await downloadBackup(job.id, downloadFallbackName(job));
      setAlert({
        variant: 'success',
        title: 'Download started',
        message: 'The backup ZIP is being saved to your device.',
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Could not download the backup.');
    } finally {
      setDownloadingId(null);
    }
  };

  const tabs: { key: 'backups' | 'config'; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
    { key: 'backups', label: 'Backups', icon: FileDown },
    { key: 'config', label: 'DB Config', icon: Server },
  ];

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {alert && <Alert {...alert} onClose={() => setAlert(null)} />}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <DatabaseBackup size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              Take Backup
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Export a database, Jenkins job tree or MailHog mailbox into its backup folder
            </p>
          </div>
        </div>
        <span className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-amber-400">
          <Settings size={13} /> Super admin only
        </span>
      </div>

      {/* Source tabs: DB / Jenkins / MailHog -------------------------- */}
      <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
        {sourceTabs.map((item) => {
          const Icon = item.icon;
          const active = source === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setSource(item.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
                active
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                  : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
              }`}
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Jenkins and MailHog each own their full panel ----------------- */}
      {source === 'JENKINS' && <SourceBackupComponent source="JENKINS" />}
      {source === 'MAILHOG' && <SourceBackupComponent source="MAILHOG" />}

      {/* DB source keeps the Backups / DB Config tabs ------------------ */}
      {source === 'DB' && (
        <>
      {/* Tabs ------------------------------------------------------- */}
      <div className="mb-6 flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2">
        {tabs.map((item) => {
          const Icon = item.icon;
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-black uppercase tracking-wider transition ${
                active
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                  : 'border border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
              }`}
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* ================= TAB: BACKUPS ================= */}
      {tab === 'backups' && (
        <div className="space-y-6">
          {/* Trigger */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <DatabaseBackup size={15} className="text-cyan-400" /> Run a backup
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  The selected formats are written into a dated subfolder of the environment's
                  backup folder.
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <Field label="Database configuration">
                <select
                  value={selectedId}
                  onChange={(event) => resetPage(() => setSelectedId(event.target.value))}
                  className={fieldClass}
                >
                  {configs.length === 0 && <option value="">No configuration yet</option>}
                  {configs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name} ({config.environment}) — {config.databaseName}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="lg:col-span-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                  Formats
                </span>
                <div className="mt-1.5 grid gap-2 sm:grid-cols-3">
                  {BACKUP_FORMATS.map((format) => (
                    <FormatOption
                      key={format}
                      format={format}
                      checked={formats.includes(format)}
                      onToggle={() => toggleFormat(format)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Table scope */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3">
              <span className="flex items-center gap-2 text-xs font-bold text-slate-300">
                <ListChecks size={15} className="text-cyan-400" />
                Scope:
                <span className="text-slate-100">{selectionLabel}</span>
              </span>
              <button
                type="button"
                onClick={openTablePicker}
                disabled={!selected}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ListChecks size={13} />
                {pickedTables.size > 0 ? 'Edit tables' : 'Choose tables'}
              </button>
            </div>

            {/* Folder state */}
            {!selected && (
              <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-xs font-semibold text-slate-500">
                Add a database configuration in the DB Config tab to enable backups.
              </div>
            )}

            {selected && !selected.folderExists && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                <span className="flex items-start gap-2 text-xs font-bold text-amber-400">
                  <TriangleAlert size={15} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Backup folder not found. Backups are disabled until it exists.
                    <code className="ml-1 block text-amber-300">{selected.baseFolderPath}</code>
                  </span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setTab('config')}
                    className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40"
                  >
                    DB Config
                  </button>
                  <button
                    type="button"
                    onClick={handleSetupFolder}
                    disabled={folderBusy}
                    className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-amber-400 disabled:opacity-50"
                  >
                    {folderBusy ? (
                      <LoaderCircle size={13} className="animate-spin" />
                    ) : (
                      <FolderPlus size={13} />
                    )}
                    Setup Folder
                  </button>
                </div>
              </div>
            )}

            {/* Action row. Always rendered so Take Backup reads as disabled rather than absent. */}
            {selected && (
              <div
                className={`mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 ${
                  selected.folderExists
                    ? 'border-emerald-500/30 bg-emerald-500/10'
                    : 'border-slate-800 bg-slate-950/60'
                }`}
              >
                <span
                  className={`flex items-center gap-2 text-xs font-bold ${
                    selected.folderExists ? 'text-emerald-400' : 'text-slate-400'
                  }`}
                >
                  <FolderCheck size={15} />
                  {selected.folderExists ? 'Folder ready —' : 'Folder not found —'}
                  <code
                    className={
                      selected.folderExists ? 'text-emerald-300' : 'text-slate-500'
                    }
                  >
                    {selected.baseFolderPath}
                  </code>
                </span>
                <button
                  type="button"
                  onClick={handleTakeBackup}
                  disabled={!canTakeBackup || taking}
                  title={
                    canTakeBackup
                      ? 'Start the backup'
                      : !selected.folderExists
                        ? 'Set up the backup folder first'
                        : 'Select at least one format'
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {taking ? (
                    <LoaderCircle size={14} className="animate-spin" />
                  ) : (
                    <FileDown size={14} />
                  )}
                  {taking ? 'Starting' : 'Take Backup'}
                </button>
              </div>
            )}

            {formats.length === 0 && (
              <p className="mt-3 text-[11px] font-semibold text-rose-400">
                Select at least one format before taking a backup.
              </p>
            )}
          </div>

          {/* Live progress ------------------------------------------------ */}
          {runningJob && liveJob && (
            <div className="rounded-3xl border border-cyan-500/30 bg-cyan-500/5 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                    <LoaderCircle size={15} className="animate-spin text-cyan-400" />
                    Backup in progress — {liveJob.dbName || 'database'}
                    <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-cyan-400">
                      {liveJob.environment ? BACKUP_ENV_LABELS[liveJob.environment] : ''}
                    </span>
                  </h2>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    {liveJob.processedTableCount ?? 0} of {liveJob.tableCount ?? 0} tables ·{' '}
                    {(liveJob.processedRows ?? 0).toLocaleString()} rows written
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleStopRun}
                  disabled={stopping}
                  className="inline-flex items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/15 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {stopping ? (
                    <LoaderCircle size={13} className="animate-spin" />
                  ) : (
                    <Square size={12} fill="currentColor" />
                  )}
                  {stopping ? 'Stopping' : 'Stop'}
                </button>
              </div>

              {/* Percentage bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-slate-500">
                  <span>Progress</span>
                  <span className="text-cyan-400">
                    {(liveJob.progressPercentage ?? 0).toFixed(1)}%
                  </span>
                </div>
                <div className="mt-1.5 h-3 w-full overflow-hidden rounded-full border border-slate-800 bg-slate-950/70">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-teal-400 transition-[width] duration-700 ease-out"
                    style={{ width: `${Math.max(1, liveJob.progressPercentage ?? 0)}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-[10px] font-semibold text-slate-500">
                  <span>
                    Elapsed {formatDuration(liveJob.elapsedTimeMs)} · Remaining{' '}
                    <span className="text-slate-300">
                      {formatDuration(liveJob.estimatedTimeMs)}
                    </span>
                  </span>
                  <span>
                    Rows {formatBytes(liveJob.sizeBytes)} written
                    {liveJob.status === 'PENDING' && ' · waiting for the worker'}
                  </span>
                </div>
              </div>

              {/* Per-table audit log */}
              {liveJob.auditLogs && liveJob.auditLogs.length > 0 && (
                <div className="mt-4">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                    Table audit
                  </span>
                  <div className="mt-1.5 max-h-48 space-y-1 overflow-y-auto rounded-2xl border border-slate-800 bg-slate-950/70 p-3">
                    {[...liveJob.auditLogs].reverse().map((line, index) => (
                      <p
                        key={`${index}-${line}`}
                        className="font-mono text-[11px] leading-relaxed text-slate-400"
                      >
                        <span className="text-cyan-500">›</span> {line}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Listing */}
          <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
            <div className="flex flex-wrap items-center justify-between gap-3 p-6">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <FileDown size={15} className="text-cyan-400" /> Backup history
                </h2>
                <p className="mt-1 text-[10px] font-semibold text-slate-500">
                  {jobs?.totalElements ?? 0} runs recorded
                </p>
              </div>
              <button
                type="button"
                onClick={() => loadJobs(page)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-3.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <RefreshCw size={13} className={jobsLoading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>

            {/* Filters */}
            <div className="grid gap-2 px-6 pb-5 md:grid-cols-3 lg:grid-cols-7">
              <div className="relative md:col-span-2 lg:col-span-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                <input
                  value={search}
                  onChange={(event) => resetPage(() => setSearch(event.target.value))}
                  placeholder="Search database, folder or user"
                  className={`${fieldClass} pl-9`}
                />
              </div>
              <select
                value={envFilter}
                onChange={(event) => resetPage(() => setEnvFilter(event.target.value))}
                className={fieldClass}
              >
                <option value="">All environments</option>
                {BACKUP_ENVIRONMENTS.map((environment) => (
                  <option key={environment} value={environment}>
                    {BACKUP_ENV_LABELS[environment]}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(event) => resetPage(() => setStatusFilter(event.target.value))}
                className={fieldClass}
              >
                <option value="">All statuses</option>
                {BACKUP_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {BACKUP_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
              <select
                value={formatFilter}
                onChange={(event) => resetPage(() => setFormatFilter(event.target.value))}
                className={fieldClass}
              >
                <option value="">All formats</option>
                {BACKUP_FORMATS.map((format) => (
                  <option key={format} value={format}>
                    {BACKUP_FORMAT_LABELS[format]}
                  </option>
                ))}
              </select>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  From
                </span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(event) => resetPage(() => setFromDate(event.target.value))}
                  className={`${fieldClass} min-w-0`}
                  aria-label="From date"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[9px] font-black uppercase tracking-wider text-slate-500">
                  To
                </span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(event) => resetPage(() => setToDate(event.target.value))}
                  className={`${fieldClass} min-w-0`}
                  aria-label="To date"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-y border-slate-800 bg-slate-900/60">
                  <tr>
                    <th className={`${headerClass} text-left`}>Date</th>
                    <th className={`${headerClass} text-left`}>Database</th>
                    <th className={`${headerClass} text-left`}>Formats</th>
                    <th className={`${headerClass} text-right`}>Rows</th>
                    <th className={`${headerClass} text-right`}>Size</th>
                    <th className={`${headerClass} text-left`}>Status</th>
                    <th className={`${headerClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobsLoading && !jobs ? (
                    Array.from({ length: 4 }).map((_, rowIndex) => (
                      <tr key={rowIndex} className="border-t border-slate-800/70">
                        {Array.from({ length: 7 }).map((__, colIndex) => (
                          <td key={colIndex} className="px-5 py-4">
                            <div className="h-4 w-full animate-pulse rounded bg-slate-800/80" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : !jobs || jobs.content.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
                            <FileDown size={24} />
                          </div>
                          <p className="text-sm font-black text-slate-200">No backups yet</p>
                          <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">
                            Configure a database in the DB Config tab, then choose your formats
                            above and take a backup.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    jobs.content.map((job) => (
                      <tr
                        key={job.id}
                        className="border-t border-slate-800/70 transition hover:bg-slate-900/40"
                      >
                        <td className="px-5 py-4">
                          <p className="text-xs font-bold text-slate-200">
                            {formatDateTime(job.createdAt)}
                          </p>
                          <p
                            className="max-w-[200px] truncate text-[10px] font-semibold text-slate-500"
                            title={job.folderPath || ''}
                          >
                            {job.folderPath || '—'}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs font-bold text-slate-200">{job.dbName || '—'}</p>
                          <span className="rounded-full border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                            {job.environment
                              ? BACKUP_ENV_LABELS[job.environment] || job.environment
                              : '—'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(job.formats || []).map((format) => (
                              <span
                                key={format}
                                className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-black tracking-wider text-cyan-400"
                              >
                                {format}
                              </span>
                            ))}
                            {!job.formats?.length && (
                              <span className="text-[10px] font-semibold text-slate-500">—</span>
                            )}
                          </div>
                          <p className="mt-1 text-[10px] font-semibold text-slate-500">
                            {job.tableCount ?? 0} tables
                          </p>
                        </td>
                        <td className="px-5 py-4 text-right text-xs font-bold text-slate-300">
                          {(job.totalRows ?? 0).toLocaleString()}
                        </td>
                        <td className="px-5 py-4 text-right text-xs font-bold text-slate-300">
                          {formatBytes(job.sizeBytes)}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                              statusTone[job.status] || statusTone.PENDING
                            }`}
                          >
                            {(job.status === 'RUNNING' || job.status === 'PENDING') && (
                              <LoaderCircle size={11} className="animate-spin" />
                            )}
                            {BACKUP_STATUS_LABELS[job.status] || job.status}
                          </span>
                          {job.status === 'FAILED' && job.errorMessage && (
                            <p className="mt-1 max-w-[220px] text-[10px] font-semibold leading-tight text-rose-400">
                              {job.errorMessage}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            {job.status === 'SUCCESS' && (
                              <button
                                type="button"
                                title="Download backup files (ZIP)"
                                onClick={() => handleDownload(job)}
                                disabled={downloadingId === job.id}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {downloadingId === job.id ? (
                                  <LoaderCircle size={14} className="animate-spin" />
                                ) : (
                                  <HardDriveDownload size={14} />
                                )}
                              </button>
                            )}
                            <button
                              type="button"
                              title="View audit log"
                              onClick={() => openJobLogs(job)}
                              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                            >
                              <ScrollText size={14} />
                            </button>
                            {confirmJobId === job.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteJob(job.id)}
                                  className="rounded-lg border border-rose-500/40 bg-rose-500/15 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25"
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmJobId(null)}
                                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                title="Delete run"
                                onClick={() => setConfirmJobId(job.id)}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {jobs && jobs.totalPages > 1 && <Pagination {...jobs} setPage={setPage} />}
          </div>
        </div>
      )}

      {/* ================= TAB: DB CONFIG ================= */}
      {tab === 'config' && (
        <div className="space-y-6">
          {/* Toolbar + form */}
          <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <Server size={15} className="text-cyan-400" /> Database configurations
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  One entry per environment, each with its own backup folder.
                </p>
              </div>
              {!showForm && (
                <button
                  type="button"
                  onClick={openAdd}
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400"
                >
                  <Plus size={14} /> Add configuration
                </button>
              )}
            </div>

            {showForm && (
              <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-950/60 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
                    {editing ? 'Edit configuration' : 'New configuration'}
                  </h3>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                  >
                    Cancel
                  </button>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <Field label="Name">
                    <input
                      value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                      className={fieldClass}
                      placeholder="Production DB"
                    />
                  </Field>
                  <Field label="Environment" hint="One folder per environment.">
                    <select
                      value={form.environment}
                      onChange={(event) =>
                        setForm({ ...form, environment: event.target.value })
                      }
                      className={fieldClass}
                    >
                      {BACKUP_ENVIRONMENTS.map((environment) => (
                        <option key={environment} value={environment}>
                          {BACKUP_ENV_LABELS[environment]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Database name">
                    <input
                      value={form.databaseName}
                      onChange={(event) => setForm({ ...form, databaseName: event.target.value })}
                      className={fieldClass}
                      placeholder="nexgent"
                    />
                  </Field>
                  <Field label="Host">
                    <input
                      value={form.host}
                      onChange={(event) => setForm({ ...form, host: event.target.value })}
                      className={fieldClass}
                      placeholder="127.0.0.1"
                    />
                  </Field>
                  <Field label="Port">
                    <input
                      type="number"
                      value={form.port ?? ''}
                      onChange={(event) =>
                        setForm({ ...form, port: Number(event.target.value) || 3306 })
                      }
                      className={fieldClass}
                    />
                  </Field>
                  <Field label="Username">
                    <input
                      value={form.username}
                      onChange={(event) => setForm({ ...form, username: event.target.value })}
                      className={fieldClass}
                      placeholder="root"
                    />
                  </Field>
                  <Field
                    label="Password"
                    hint={
                      editing && editing.passwordSet
                        ? 'Leave blank to keep the saved password.'
                        : undefined
                    }
                  >
                    <input
                      type="password"
                      value={form.password || ''}
                      onChange={(event) => setForm({ ...form, password: event.target.value })}
                      className={fieldClass}
                      placeholder={editing && editing.passwordSet ? '••••••••' : ''}
                    />
                  </Field>
                  <Field label="Backup folder" hint="Must exist, or create it with Setup Folder.">
                    <input
                      value={form.baseFolderPath}
                      onChange={(event) =>
                        setForm({ ...form, baseFolderPath: event.target.value })
                      }
                      className={fieldClass}
                      placeholder="D:\\backups\\prod"
                    />
                  </Field>
                  <Field label="Active">
                    <div className="flex h-[46px] items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, active: !form.active })}
                        className={`flex h-5 w-9 items-center rounded-full px-0.5 transition ${
                          form.active ? 'bg-cyan-500' : 'bg-slate-700'
                        }`}
                        aria-pressed={form.active}
                      >
                        <span
                          className={`h-4 w-4 rounded-full bg-slate-950 transition ${
                            form.active ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="text-xs font-semibold text-slate-400">
                        {form.active ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </Field>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:text-slate-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveForm}
                    disabled={saving}
                    className="rounded-xl bg-cyan-500 px-5 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {saving ? 'Saving' : editing ? 'Save changes' : 'Add configuration'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Config list */}
          <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-y border-slate-800 bg-slate-900/60">
                  <tr>
                    <th className={`${headerClass} text-left`}>Environment</th>
                    <th className={`${headerClass} text-left`}>Connection</th>
                    <th className={`${headerClass} text-left`}>Backup folder</th>
                    <th className={`${headerClass} text-left`}>State</th>
                    <th className={`${headerClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-slate-500">
                            <Server size={24} />
                          </div>
                          <p className="text-sm font-black text-slate-200">
                            No database configured
                          </p>
                          <p className="max-w-sm text-xs font-semibold leading-relaxed text-slate-500">
                            Add a configuration so this environment has somewhere to write its
                            backups.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    configs.map((config) => (
                      <tr
                        key={config.id}
                        className="border-t border-slate-800/70 transition hover:bg-slate-900/40"
                      >
                        <td className="px-5 py-4">
                          <p className="text-xs font-bold text-slate-200">{config.name}</p>
                          <span className="rounded-full border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                            {BACKUP_ENV_LABELS[config.environment] || config.environment}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-xs font-semibold text-slate-300">
                            {config.host}:{config.port}/{config.databaseName}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-500">
                            {config.username} · {config.passwordSet ? 'password saved' : 'no password'}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          <p
                            className="max-w-[240px] truncate text-xs font-semibold text-slate-400"
                            title={config.baseFolderPath}
                          >
                            {config.baseFolderPath}
                          </p>
                          {config.folderExists ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-400">
                              <FolderCheck size={11} /> Folder found
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-400">
                              <TriangleAlert size={11} /> Folder missing
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${
                              config.active
                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                : 'border-slate-700 bg-slate-800/60 text-slate-400'
                            }`}
                          >
                            {config.active ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {!config.folderExists && (
                              <button
                                type="button"
                                onClick={async () => {
                                  const response = await setupBackupFolder(config.id);
                                  if (response.success && response.data?.exists) {
                                    setAlert({
                                      variant: 'success',
                                      title: 'Folder created',
                                      message: response.data.message,
                                    });
                                    loadConfigs();
                                  } else {
                                    showError(response.message || 'Could not create the folder.');
                                  }
                                }}
                                className="rounded-lg border border-amber-500/40 bg-amber-500/15 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-amber-300 transition hover:bg-amber-500/25"
                              >
                                Setup Folder
                              </button>
                            )}
                            <button
                              type="button"
                              title="Test connection"
                              onClick={() => handleTest(config.id)}
                              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                            >
                              {testingId === config.id ? (
                                <LoaderCircle size={14} className="animate-spin" />
                              ) : (
                                <TestTubeDiagonal size={14} />
                              )}
                            </button>
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => openEdit(config)}
                              className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
                            >
                              <Pencil size={14} />
                            </button>
                            {confirmConfigId === config.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteConfig(config.id)}
                                  className="rounded-lg border border-rose-500/40 bg-rose-500/15 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-rose-300 transition hover:bg-rose-500/25"
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmConfigId(null)}
                                  className="rounded-lg border border-slate-800 bg-slate-950/60 px-2.5 py-2 text-[10px] font-black uppercase tracking-wider text-slate-500 transition hover:text-slate-300"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => setConfirmConfigId(config.id)}
                                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-rose-500/40 hover:text-rose-400"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* ================= JOB AUDIT LOG ================= */}
      {logsJobId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-6 py-4">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <ScrollText size={16} className="text-cyan-400" />
                  Audit log — {logsJob?.dbName || 'backup'}
                </h3>
                <p className="mt-0.5 text-[10px] font-semibold text-slate-500">
                  {logsJob ? (
                    <>
                      {formatDateTime(logsJob.startedAt || logsJob.createdAt)}
                      {logsJob.environment ? ` · ${BACKUP_ENV_LABELS[logsJob.environment]}` : ''}
                      {` · ${(logsJob.tableCount ?? 0)} tables · ${(logsJob.totalRows ?? 0).toLocaleString()} rows`}
                    </>
                  ) : (
                    'Loading'
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={closeJobLogs}
                title="Close"
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <X size={15} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {logsLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                  <LoaderCircle size={22} className="animate-spin text-cyan-400" />
                  <p className="text-xs font-semibold text-slate-500">Loading log…</p>
                </div>
              ) : logsJob?.status === 'FAILED' && logsJob.errorMessage ? (
                <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-wider text-rose-400">
                    Failure
                  </p>
                  <p className="mt-1 font-mono text-xs leading-relaxed text-rose-300">
                    {logsJob.errorMessage}
                  </p>
                </div>
              ) : null}

              {logsJob?.auditLogs && logsJob.auditLogs.length > 0 ? (
                <div className="space-y-1 rounded-2xl border border-slate-800 bg-slate-950/70 p-3">
                  {[...logsJob.auditLogs].reverse().map((line, index) => (
                    <p
                      key={`${index}-${line}`}
                      className="font-mono text-[11px] leading-relaxed text-slate-400"
                    >
                      <span className="text-cyan-500">›</span> {line}
                    </p>
                  ))}
                </div>
              ) : (
                !logsLoading && (
                  <p className="py-10 text-center text-xs font-semibold text-slate-500">
                    No audit lines were recorded for this run.
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= TABLE PICKER ================= */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-6 py-4">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-black text-slate-100">
                  <ListChecks size={16} className="text-cyan-400" /> Choose tables
                </h3>
                <p className="mt-0.5 text-[10px] font-semibold text-slate-500">
                  {tableList.length > 0
                    ? `${pickedTables.size} of ${tableList.length} selected`
                    : 'Reading table list'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTablePicker}
                title="Close"
                className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-slate-400 transition hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <X size={15} />
              </button>
            </div>

            <div className="space-y-3 px-6 py-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                <input
                  value={tableSearch}
                  onChange={(event) => setTableSearch(event.target.value)}
                  placeholder="Search tables"
                  className={`${fieldClass} pl-9`}
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllVisible}
                  disabled={tablesLoading || visibleTables.length === 0}
                  className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={deselectAllVisible}
                  disabled={tablesLoading || visibleTables.length === 0}
                  className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-300 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-40"
                >
                  Deselect all
                </button>
                <span className="ml-auto text-[10px] font-black uppercase tracking-wider text-slate-500">
                  {tableList.length} table{tableList.length === 1 ? '' : 's'} in database
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-2">
              {tablesLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                  <LoaderCircle size={22} className="animate-spin text-cyan-400" />
                  <p className="text-xs font-semibold text-slate-500">Reading tables…</p>
                </div>
              ) : tablePickerError ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                  <TriangleAlert size={22} className="text-amber-400" />
                  <p className="max-w-xs text-xs font-semibold leading-relaxed text-amber-400">
                    {tablePickerError}
                  </p>
                </div>
              ) : visibleTables.length === 0 ? (
                <p className="py-10 text-center text-xs font-semibold text-slate-500">
                  No table matches “{tableSearch}”.
                </p>
              ) : (
                <div className="space-y-1 pb-2">
                  {visibleTables.map((name) => {
                    const checked = pickedTables.has(name);
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => toggleTable(name)}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                          checked
                            ? 'border-cyan-500/40 bg-cyan-500/10'
                            : 'border-slate-800 bg-slate-950/60 hover:border-slate-700'
                        }`}
                      >
                        <span
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                            checked ? 'border-cyan-400 bg-cyan-500 text-slate-950' : 'border-slate-600'
                          }`}
                        >
                          {checked && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span
                          className={`truncate font-mono text-xs font-semibold ${
                            checked ? 'text-slate-100' : 'text-slate-400'
                          }`}
                        >
                          {name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-6 py-4">
              <span className="text-[10px] font-semibold text-slate-500">
                {pickedTables.size === tableList.length && tableList.length > 0
                  ? 'All tables will be exported.'
                  : `${pickedTables.size} table(s) will be exported.`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeTablePicker}
                  className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-400 transition hover:text-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyTableSelection}
                  disabled={pickedTables.size === 0}
                  title={pickedTables.size === 0 ? 'Select at least one table' : 'Apply selection'}
                  className="rounded-xl bg-cyan-500 px-5 py-2.5 text-[10px] font-black uppercase tracking-wider text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default TakeBackupComponent;
